import type { Data } from '@puckeditor/core'
import type { GetServerSideProps } from 'next'
import * as React from 'react'

import { canShowQuickEdit } from '@/lib/page-editor-access'
import {
  type PublishedChromeConfig,
  hasNonFsdsPuckContent,
  hasRenderablePuckContent,
  preparePublishedPuckData,
} from '@/lib/puck-data-utils'
import { useBranding } from '@/hooks/use-branding'
import useLocale from '@/hooks/use-locale'

import { instructors, nonFSDSInstructors } from '@/data/instructors'
import { instructorsEng } from '@/data/instructors-en'

import AdminQuickEditFab from '@/components/admin/admin-quick-edit-fab'
import ViewTracker from '@/components/common/view-tracker'
import FsdsLandingPage from '@/components/homepage/fsds-landing-page'
import NonFsdsHomepageWithTemplate from '@/components/homepage/non-fsds-homepage-with-template'
import SEO from '@/components/seo/SEO'
import RootContainer from '@/containers/root-container'

import { useAuth } from '@/contexts/auth-context'
import { useCan } from '@/contexts/feature-flag-context'
import { communityConfigService } from '@/services/community-config.service'
import { communityPageService } from '@/services/community-page.service'
import { isFsdsHostname } from '@/utils/domain-branding-resolver'
import { getTenantFromRequest } from '@/utils/tenant.utils'

interface HomePageProps {
  puckData: Data | null
  publishedChrome: PublishedChromeConfig | null
  communityId: string | null
}

const nullHomepageProps: HomePageProps = {
  puckData: null,
  publishedChrome: null,
  communityId: null,
}

function toPublishedProps(
  data: Data,
  communityId: string
): { props: HomePageProps } | null {
  const prepared = preparePublishedPuckData(data)
  if (!hasRenderablePuckContent(prepared.data)) {
    return null
  }
  return { props: { puckData: prepared.data, publishedChrome: prepared.chrome, communityId } }
}

export const getServerSideProps: GetServerSideProps<HomePageProps> = async context => {
  const host = context.req.headers.host ?? ''

  if (isFsdsHostname(host)) {
    return { props: nullHomepageProps }
  }

  try {
    const tenantId = getTenantFromRequest(context.req)
    let communityId: string | null = null

    if (tenantId !== 'default') {
      const bySubdomain = await communityConfigService.getBySubdomain(tenantId)
      communityId = bySubdomain?.id ?? null
    }

    if (!communityId) {
      const byDomain = await communityConfigService.getByDomain(host)
      communityId = byDomain?.id ?? null
    }

    if (!communityId) {
      return { props: nullHomepageProps }
    }

    const published = await communityPageService.getPublishedPage(communityId, 'homepage')

    if (published?.data && hasNonFsdsPuckContent(published.data as Data)) {
      const publishedProps = toPublishedProps(published.data as Data, communityId)
      if (publishedProps) {
        return publishedProps
      }
    }

    // Homepage is now seeded automatically upon community creation
    // We don't try to seed it dynamically anymore.

    return {
      props: {
        puckData: null,
        publishedChrome: null,
        communityId,
      },
    }
  } catch {
    return { props: nullHomepageProps }
  }
}

export default function HomePage({ puckData, publishedChrome }: Partial<HomePageProps> = {}) {
  const { lang } = useLocale()
  const { user } = useAuth()
  const { isFsdsDomain } = useBranding()
  const canEdit = useCan('mode.editor')

  const [isCourseEnrolled, setIsCourseEnrolled] = React.useState(false)
  // Puck template manages its own chrome — derive directly from SSR props.
  const hasTemplate = puckData != null

  const showQuickEdit = canShowQuickEdit({ user, canEdit, slug: 'homepage' })
  const instructorsList = lang === 'vi' ? instructors : instructorsEng
  const isNonFsds = !isFsdsDomain

  React.useEffect(() => {
    if (!user) {
      return
    }

    const courseSlug = user.courseUsers?.map(courseUser => courseUser.course.slug)
    if (courseSlug) {
      setIsCourseEnrolled(courseSlug.some(slug => slug.includes('mlops-1')))
    }
  }, [user])

  const hideChrome = isNonFsds && hasTemplate

  return (
    <RootContainer showHeader={!hideChrome} showFooter={!hideChrome}>
      <ViewTracker entityType="page" entityId="homepage" />
      {isNonFsds ? <SEO /> : <SEO title="Full Stack Data Science - Learn Data Science, AI, and Machine Learning" />}
      {showQuickEdit && isNonFsds && <AdminQuickEditFab currentPageSlug="homepage" />}
      {isNonFsds ? (
        <NonFsdsHomepageWithTemplate
          lang={lang}
          isCourseEnrolled={isCourseEnrolled}
          instructorsList={nonFSDSInstructors}
          initialPuckData={puckData}
          initialPublishedChrome={publishedChrome}
        />
      ) : (
        <FsdsLandingPage lang={lang} isCourseEnrolled={isCourseEnrolled} instructorsList={instructorsList} />
      )}
    </RootContainer>
  )
}
